import { apiClient } from "./client";

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface UserResponse {
  id: number;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_admin: boolean;
  is_super_admin?: boolean;
  tenant_id?: number | null;
}

export interface InviteResponse {
  id: number;
  token: string;
  email: string;
  full_name: string | null;
  is_admin: boolean;
  expires_at: string;
  used_at: string | null;
  created_by_id: number | null;
  created_at: string;
}

export interface InvitePublicView {
  email: string;
  full_name: string | null;
  is_admin: boolean;
  expires_at: string;
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  // FastAPI OAuth2PasswordRequestForm očekuje x-www-form-urlencoded
  const form = new URLSearchParams();
  form.append("username", email);
  form.append("password", password);

  const { data } = await apiClient.post<LoginResponse>("/api/auth/login", form, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
  return data;
}

export async function logoutBackend(refreshToken: string): Promise<void> {
  // Idempotent — backend ne baca error ako token nije validan
  await apiClient.post("/api/auth/logout", { refresh_token: refreshToken }).catch(() => {
    // ignoriši network/auth greške — frontend svejedno briše lokalne tokene
  });
}

export async function getMe(): Promise<UserResponse> {
  const { data } = await apiClient.get<UserResponse>("/api/auth/me");
  return data;
}

export interface TenantContext {
  id: number;
  slug: string;
  name: string;
  is_default: boolean;
}

export async function getTenantContext(): Promise<TenantContext> {
  const { data } = await apiClient.get<TenantContext>("/api/auth/context");
  return data;
}

export interface HealthResponse {
  status: string;
  database: string;
  version: string;
}

export async function getHealth(): Promise<HealthResponse> {
  const { data } = await apiClient.get<HealthResponse>("/api/health");
  return data;
}

export interface MinewESLStatus {
  connected: boolean;
  base_url: string;
  username: string;
  token_age_seconds: number | null;
  fields_count: number | null;
  request_rate_per_min: number;
  last_error: string | null;
  silent_session_warning: boolean;
  silent_session_hint: string | null;
}

export async function getMinewESLStatus(): Promise<MinewESLStatus> {
  const { data } = await apiClient.get<MinewESLStatus>("/api/minewesl/status");
  return data;
}

export async function reloginMinewESL(): Promise<MinewESLStatus> {
  const { data } = await apiClient.post<MinewESLStatus>("/api/minewesl/relogin");
  return data;
}

// --------------------------------------------------------------------------
// Invites + users management (admin only)
// --------------------------------------------------------------------------

export async function listInvites(showUsed = false): Promise<InviteResponse[]> {
  const { data } = await apiClient.get<InviteResponse[]>("/api/auth/invites", {
    params: { show_used: showUsed },
  });
  return data;
}

export interface CreateInvitePayload {
  email: string;
  full_name?: string;
  is_admin?: boolean;
  ttl_days?: number;
}

export async function createInvite(
  payload: CreateInvitePayload,
): Promise<InviteResponse> {
  const { data } = await apiClient.post<InviteResponse>("/api/auth/invites", payload);
  return data;
}

export async function revokeInvite(id: number): Promise<void> {
  await apiClient.delete(`/api/auth/invites/${id}`);
}

export async function getInviteInfo(token: string): Promise<InvitePublicView> {
  const { data } = await apiClient.get<InvitePublicView>(
    `/api/auth/invites/${encodeURIComponent(token)}/info`,
  );
  return data;
}

export interface AcceptInvitePayload {
  token: string;
  password: string;
  full_name?: string;
}

export async function acceptInvite(
  payload: AcceptInvitePayload,
): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>(
    "/api/auth/accept-invite",
    payload,
  );
  return data;
}

export async function listUsers(): Promise<UserResponse[]> {
  const { data } = await apiClient.get<UserResponse[]>("/api/auth/users");
  return data;
}

export async function deactivateUser(id: number): Promise<UserResponse> {
  const { data } = await apiClient.post<UserResponse>(
    `/api/auth/users/${id}/deactivate`,
  );
  return data;
}

export async function activateUser(id: number): Promise<UserResponse> {
  const { data } = await apiClient.post<UserResponse>(
    `/api/auth/users/${id}/activate`,
  );
  return data;
}
